import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { updateHHToken } from '../services/api';

interface HHKeySetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const HHKeySetupModal: React.FC<HHKeySetupModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ apiKey?: string }>({});

  const validateApiKey = (key: string): string | undefined => {
    if (!key.trim()) {
      return 'API ключ обязателен';
    }
    if (key.length < 10) {
      return 'API ключ должен содержать минимум 10 символов';
    }
    if (key.length > 100) {
      return 'API ключ не должен превышать 100 символов';
    }
    if (!/^[a-zA-Z0-9\-_]+$/.test(key)) {
      return 'API ключ может содержать только латинские символы, цифры, дефисы и подчеркивания';
    }
    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateApiKey(apiKey);
    if (validationError) {
      setErrors({ apiKey: validationError });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Используем единый путь, применяемый для HH токена в проекте
      await updateHHToken(apiKey);
      toast.success('API ключ успешно сохранен');
      setApiKey('');
      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Ошибка сохранения API ключа';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setApiKey('');
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Установить API ключ HH.ru
          </h3>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 mb-2">
                API ключ HH.ru
              </label>
              <input
                type="password"
                id="apiKey"
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  if (errors.apiKey) {
                    setErrors({ ...errors, apiKey: undefined });
                  }
                }}
                placeholder="Введите API ключ HH.ru"
                disabled={loading}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.apiKey ? 'border-red-300' : 'border-gray-300'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              />
              {errors.apiKey && (
                <p className="mt-1 text-sm text-red-600">{errors.apiKey}</p>
              )}
            </div>

            <div className="text-sm text-gray-600">
              <p>API ключ будет использоваться для доступа к сервисам HH.ru от имени администратора.</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading || !apiKey.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HHKeySetupModal;
